import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  X, Search, BookOpen, Type, Image, BarChart2, Layout, Zap,
  Download, Keyboard, Brain, Code, FileText, Settings, Star,
  Layers, Sparkles, Eye, ChevronRight, Table, Video,
} from 'lucide-react';

// ─── Helper Components ────────────────────────────────────────────────────────

const CodeBlock: React.FC<{ code: string; lang?: string }> = ({ code, lang = 'typescript' }) => (
  <div className="rounded-xl overflow-hidden my-4 border border-gray-700 text-left">
    {lang && (
      <div className="bg-gray-800 px-4 py-1.5 flex items-center gap-2 border-b border-gray-700">
        <span className="text-xs text-gray-400 font-mono">{lang}</span>
      </div>
    )}
    <pre className="bg-gray-950 text-gray-100 p-4 text-xs font-mono overflow-x-auto leading-relaxed whitespace-pre">
      {code}
    </pre>
  </div>
);

const Tag: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = 'blue' }) => {
  const colors: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    orange: 'bg-orange-100 text-orange-700 border-orange-200',
    gray: 'bg-gray-100 text-gray-600 border-gray-200',
    red: 'bg-red-100 text-red-600 border-red-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-md border text-xs font-medium ${colors[color] || colors.blue}`}>
      {children}
    </span>
  );
};

const KbdKey: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="inline-flex items-center px-2 py-1 rounded-md bg-white border border-gray-300 text-gray-700 text-xs font-mono shadow-sm">
    {children}
  </kbd>
);

const SectionTitle: React.FC<{ children: React.ReactNode; subtitle?: string }> = ({ children, subtitle }) => (
  <div className="mb-6 pb-4 border-b border-gray-100">
    <h2 className="text-2xl font-bold text-gray-900">{children}</h2>
    {subtitle && <p className="mt-1 text-gray-500 text-sm">{subtitle}</p>}
  </div>
);

const H3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-base font-semibold text-gray-800 mt-8 mb-3 flex items-center gap-2">
    <span className="w-1 h-4 bg-blue-500 rounded-full shrink-0" />
    {children}
  </h3>
);

const InfoBox: React.FC<{ type?: 'info' | 'warn' | 'tip'; children: React.ReactNode }> = ({ type = 'info', children }) => {
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warn: 'bg-amber-50 border-amber-200 text-amber-800',
    tip: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  };
  const icons = { info: 'ℹ️', warn: '⚠️', tip: '💡' };
  return (
    <div className={`border rounded-xl p-4 mb-4 text-sm ${styles[type]}`}>
      <span className="mr-2">{icons[type]}</span>
      {children}
    </div>
  );
};

const BlockCard: React.FC<{
  type: string;
  label: string;
  description: string;
  props?: string[];
  note?: string;
  aiSupport?: boolean;
  exportSupport?: boolean;
}> = ({ type, label, description, props = [], note, aiSupport = true, exportSupport = true }) => (
  <div className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all bg-white group">
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2">
        <code className="text-xs font-mono bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-md text-blue-700">{type}</code>
        <span className="font-semibold text-gray-900 text-sm">{label}</span>
      </div>
      <div className="flex gap-1.5">
        {aiSupport && <Tag color="purple">AI</Tag>}
        {exportSupport && <Tag color="green">Export</Tag>}
        {!exportSupport && <Tag color="gray">No Export</Tag>}
      </div>
    </div>
    <p className="text-sm text-gray-600 mb-3 leading-relaxed">{description}</p>
    {props.length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        {props.map(p => (
          <span key={p} className="text-xs bg-gray-50 border border-gray-200 px-2 py-0.5 rounded font-mono text-gray-500">
            {p}
          </span>
        ))}
      </div>
    )}
    {note && (
      <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 px-3 py-2 rounded-lg">
        ⚡ {note}
      </div>
    )}
  </div>
);

// ─── Navigation Structure ─────────────────────────────────────────────────────

interface NavItem { id: string; title: string; icon: React.FC<{ className?: string }>; keywords?: string[]; }
interface NavGroup { label: string; items: NavItem[]; }

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Getting Started',
    items: [
      { id: 'introduction', title: 'Introduction', icon: BookOpen, keywords: ['overview', 'about', 'what is', 'features'] },
      { id: 'quick-start', title: 'Quick Start', icon: Zap, keywords: ['setup', 'install', 'begin', 'usage', 'example'] },
      { id: 'configuration', title: 'Configuration', icon: Settings, keywords: ['config', 'props', 'options', 'interface'] },
    ],
  },
  {
    label: 'Block Reference',
    items: [
      { id: 'text-blocks', title: 'Text & Typography', icon: Type, keywords: ['text', 'heading', 'paragraph', 'list', 'quote', 'code', 'divider', 'bullet', 'checklist'] },
      { id: 'media-blocks', title: 'Media & Visual', icon: Image, keywords: ['image', 'video', 'youtube', 'signature', 'media', 'embed'] },
      { id: 'chart-blocks', title: 'Charts & Data', icon: BarChart2, keywords: ['chart', 'table', 'metrics', 'kpi', 'progress', 'timeline', 'data', 'bar', 'pie', 'line'] },
      { id: 'design-blocks', title: 'Design Elements', icon: Star, keywords: ['button', 'badge', 'callout', 'spacer', 'shape', 'alert', 'tag'] },
      { id: 'layout-blocks', title: 'Layout & Structure', icon: Layout, keywords: ['columns', 'card', 'page-break', 'section', 'layout', 'grid', 'container'] },
      { id: 'special-blocks', title: 'Special Blocks', icon: Sparkles, keywords: ['markdown', 'icon', 'special', 'section-marker', 'ai'] },
    ],
  },
  {
    label: 'Features',
    items: [
      { id: 'variables', title: 'Variable System', icon: Code, keywords: ['variable', 'placeholder', 'dynamic', 'substitution', 'template', '{{'] },
      { id: 'ai-regen', title: 'AI Regeneration', icon: Brain, keywords: ['ai', 'regenerate', 'artificial intelligence', 'section', 'gpt', 'llm'] },
      { id: 'export', title: 'Export & Download', icon: Download, keywords: ['export', 'pdf', 'docx', 'html', 'download', 'word'] },
      { id: 'page-templates', title: 'Page Templates', icon: FileText, keywords: ['template', 'page', 'size', 'a4', 'letter', 'portrait', 'landscape'] },
      { id: 'keyboard', title: 'Keyboard Shortcuts', icon: Keyboard, keywords: ['keyboard', 'shortcut', 'hotkey', 'ctrl', 'key'] },
      { id: 'advanced', title: 'Advanced Features', icon: Layers, keywords: ['undo', 'redo', 'zoom', 'snap', 'grid', 'lock', 'font', 'clipboard', 'layer', 'z-index'] },
    ],
  },
];

// ─── Section Content ──────────────────────────────────────────────────────────

const SECTIONS: Record<string, React.ReactNode> = {

  introduction: (
    <div>
      <SectionTitle subtitle="A powerful, AI-ready block-based template editor for building professional documents and reports.">
        Template Editor
      </SectionTitle>

      <div className="grid grid-cols-3 gap-3 mb-8">
        {[
          { label: '27+ Block Types', desc: 'Text, media, charts, layout, and more', emoji: '🧱' },
          { label: '15+ Page Templates', desc: 'A4, Letter, Presentation, Social, and more', emoji: '📄' },
          { label: 'AI Integration', desc: 'Section-aware content regeneration', emoji: '🤖' },
          { label: 'Export System', desc: 'HTML, PDF, and DOCX export', emoji: '📤' },
          { label: 'Variable System', desc: '{{variable}} dynamic content substitution', emoji: '⚡' },
          { label: 'Drag & Resize', desc: 'Absolute positioning with snap-to-grid', emoji: '🎯' },
        ].map(f => (
          <div key={f.label} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-200 hover:border-blue-200 transition-colors">
            <div className="text-2xl mb-2">{f.emoji}</div>
            <div className="font-semibold text-gray-900 text-sm mb-1">{f.label}</div>
            <div className="text-gray-500 text-xs leading-relaxed">{f.desc}</div>
          </div>
        ))}
      </div>

      <H3>Core Architecture</H3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4">
        The editor is a single React component (<code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono text-blue-700">ModernTemplateEditor</code>) that manages blocks, pages, and state. It operates in two modes:
      </p>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="border-l-4 border-blue-500 pl-4 py-1">
          <div className="font-semibold text-gray-900 text-sm">Template Mode</div>
          <div className="text-xs text-gray-500 mt-1 leading-relaxed">Full editing with save/load from Supabase. Used by admins to create reusable templates.</div>
        </div>
        <div className="border-l-4 border-emerald-500 pl-4 py-1">
          <div className="font-semibold text-gray-900 text-sm">Document Mode</div>
          <div className="text-xs text-gray-500 mt-1 leading-relaxed">End-user document creation from templates. Supports variable population and export.</div>
        </div>
      </div>

      <H3>Block System Overview</H3>
      <p className="text-gray-600 text-sm leading-relaxed">
        Every piece of content in the editor is a <strong>Block</strong>. Each block has a unique <code className="bg-gray-100 px-1 rounded text-xs font-mono">id</code>, a <code className="bg-gray-100 px-1 rounded text-xs font-mono">type</code>, a <code className="bg-gray-100 px-1 rounded text-xs font-mono">content</code> object, and a <code className="bg-gray-100 px-1 rounded text-xs font-mono">style</code> object. Blocks are absolutely positioned on the canvas using <code className="bg-gray-100 px-1 rounded text-xs font-mono">x</code>, <code className="bg-gray-100 px-1 rounded text-xs font-mono">y</code>, <code className="bg-gray-100 px-1 rounded text-xs font-mono">width</code>, and <code className="bg-gray-100 px-1 rounded text-xs font-mono">height</code>.
      </p>

      <H3>Block Base Interface</H3>
      <CodeBlock code={`interface Block {
  id: string;
  type: BlockType;        // One of 27+ block type strings
  content: any;           // Type-specific content object
  style: BlockStyle;      // Position, size, and visual styles
  locked?: boolean;       // Prevents editing when true
  hidden?: boolean;       // Hidden from canvas and exports
  zIndex?: number;        // Stacking order
}`} />
    </div>
  ),

  'quick-start': (
    <div>
      <SectionTitle subtitle="Get the editor running in your React application in minutes.">
        Quick Start
      </SectionTitle>

      <H3>Basic Usage</H3>
      <CodeBlock code={`import ModernTemplateEditor from '@features/Editor/components/ModernEditor/ModernTemplateEditor';

<ModernTemplateEditor
  config={{
    context: 'document',
    title: 'My Document',
    showToolbar: true,
    showPropertyPanel: true,
    allowExport: true,
    predefinedVariables: [
      { name: 'client_name', type: 'text', defaultValue: 'Client Name' },
      { name: 'date', type: 'date', defaultValue: new Date().toISOString() },
    ],
  }}
  onSave={async (data) => {
    console.log('Saved:', data);
    return { success: true };
  }}
/>`} />

      <H3>Loading a Template</H3>
      <CodeBlock code={`<ModernTemplateEditor
  config={{
    context: 'document',
    templateId: 'your-template-uuid',   // Loads from Supabase
    predefinedVariables: [
      { name: 'client_name', type: 'text', defaultValue: 'Acme Corp' },
    ],
  }}
/>`} />

      <H3>Template Authoring Mode (Admin)</H3>
      <CodeBlock code={`<ModernTemplateEditor
  config={{
    context: 'template',
    showToolbar: true,
    showPropertyPanel: true,
    allowAIRegeneration: true,
    allowExport: true,
  }}
  onSave={async (data) => {
    const result = await templateService.saveTemplate(data);
    return { success: true, id: result.id };
  }}
/>`} />

      <H3>Using the Editor Ref</H3>
      <CodeBlock code={`import type { TemplateEditorHandle } from '@features/Editor/types/editor';

const editorRef = useRef<TemplateEditorHandle>(null);

<ModernTemplateEditor ref={editorRef} config={{ ... }} />

// Trigger actions programmatically
await editorRef.current?.save();
await editorRef.current?.exportHTML();
await editorRef.current?.exportPDF();
await editorRef.current?.exportDOCX();
const state = editorRef.current?.getEditorState();`} />
    </div>
  ),

  configuration: (
    <div>
      <SectionTitle subtitle="All available options for the TemplateEditorConfig interface.">
        Configuration
      </SectionTitle>

      <H3>TemplateEditorConfig Properties</H3>
      <div className="overflow-hidden rounded-xl border border-gray-200 mb-6">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-700">Property</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-700">Type</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-700">Default</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-700">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {[
              ['context', '"template" | "document"', '"document"', 'Operating mode'],
              ['templateId', 'string', 'undefined', 'ID of template to load from database'],
              ['title', 'string', '"Untitled"', 'Document or template title'],
              ['showToolbar', 'boolean', 'true', 'Show the top toolbar'],
              ['showPropertyPanel', 'boolean', 'true', 'Show the right-side property panel'],
              ['allowExport', 'boolean', 'true', 'Enable export functionality'],
              ['allowAIRegeneration', 'boolean', 'false', 'Enable AI content regeneration panel'],
              ['predefinedVariables', 'TemplateVariable[]', '[]', 'Variables available for {{substitution}}'],
              ['defaultPageTemplate', 'string', '"a4-portrait"', 'Default page size on creation'],
              ['onAIRegenerate', '(req) => Promise<{blocks}>', 'undefined', 'AI regeneration handler callback'],
            ].map(([prop, type, def, desc]) => (
              <tr key={prop as string} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-blue-700">{prop}</td>
                <td className="px-4 py-3 font-mono text-gray-500">{type}</td>
                <td className="px-4 py-3 font-mono text-gray-400">{def}</td>
                <td className="px-4 py-3 text-gray-600">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H3>TemplateVariable Interface</H3>
      <CodeBlock code={`interface TemplateVariable {
  name: string;        // Identifier used in {{name}} — no spaces
  type: 'text' | 'number' | 'date' | 'boolean' | 'list';
  defaultValue?: string;
  label?: string;      // Display name in the UI
  required?: boolean;
  options?: string[];  // Values for type: 'list'
}`} />

      <H3>TemplateEditorProps</H3>
      <CodeBlock code={`interface TemplateEditorProps {
  config: TemplateEditorConfig;
  onSave?: (data: EditorSaveData) => Promise<EditorSaveResult>;
  className?: string;
  style?: React.CSSProperties;
}`} />
    </div>
  ),

  'text-blocks': (
    <div>
      <SectionTitle subtitle="Blocks for text content, headings, lists, quotes, and code.">
        Text & Typography Blocks
      </SectionTitle>
      <div className="grid grid-cols-1 gap-3">
        <BlockCard type="text" label="Text / Paragraph" description="Standard paragraph text with full rich-text formatting. The most commonly used block." props={['content', 'fontSize', 'fontWeight', 'fontFamily', 'color', 'textAlign', 'lineHeight', 'letterSpacing']} note="Supports {{variable_name}} substitution" />
        <BlockCard type="heading" label="Heading" description="Large document heading for section titles. Supports levels 1–6." props={['content', 'level', 'fontSize', 'fontWeight', 'color', 'textAlign']} note="Use heading level 1 for main title, 2–3 for sections" />
        <BlockCard type="subheading" label="Subheading" description="Medium-sized heading used under main headings for subsections." props={['content', 'fontSize', 'fontWeight', 'color', 'textAlign']} />
        <BlockCard type="bullet-list" label="Bullet List" description="Unordered list with customizable bullet style, indent levels, and item formatting." props={['items[]', 'bulletStyle', 'indentLevel', 'fontSize', 'color']} />
        <BlockCard type="numbered-list" label="Numbered List" description="Ordered list with automatic numbering. Supports nested lists and custom start numbers." props={['items[]', 'startNumber', 'numberStyle', 'indentLevel', 'fontSize']} />
        <BlockCard type="checklist" label="Checklist" description="Task list with checkboxes. Each item stores checked/unchecked state independently." props={['items[].text', 'items[].checked', 'fontSize', 'color']} />
        <BlockCard type="blockquote" label="Blockquote" description="Styled quote with left accent bar. Perfect for testimonials, citations, or highlighted statements." props={['content', 'attribution', 'accentColor', 'fontSize', 'style']} note="Supports {{variable_name}} in content and attribution" />
        <BlockCard type="code" label="Code Block" description="Syntax-highlighted code display with language label and monospace font." props={['content', 'language', 'theme', 'showLineNumbers', 'fontSize']} aiSupport={false} />
        <BlockCard type="divider" label="Divider" description="Visual horizontal rule separator. Fully customizable line style, thickness, and color." props={['style', 'thickness', 'color', 'width', 'margin']} aiSupport={false} exportSupport={false} />
      </div>
    </div>
  ),

  'media-blocks': (
    <div>
      <SectionTitle subtitle="Blocks for images, videos, embedded content, and signatures.">
        Media & Visual Blocks
      </SectionTitle>
      <div className="grid grid-cols-1 gap-3">
        <BlockCard type="image" label="Image" description="Static image block supporting URL and base64 data URI sources. Includes caption, alt text, and resize controls." props={['src', 'alt', 'caption', 'objectFit', 'borderRadius', 'border', 'opacity']} note="Use base64 data URIs for full export compatibility" />
        <BlockCard type="video" label="Video Player" description="HTML5 video player with autoplay, mute, loop, and controls options." props={['src', 'poster', 'autoPlay', 'muted', 'loop', 'controls', 'caption']} exportSupport={false} aiSupport={false} />
        <BlockCard type="youtube" label="YouTube Embed" description="Embedded YouTube video via iframe. Accepts a video ID or full YouTube URL." props={['videoId', 'autoPlay', 'caption', 'aspectRatio']} exportSupport={false} aiSupport={false} />
        <BlockCard type="signature" label="Signature Field" description="Signature input with typed and drawn signature modes. Includes signer name, title, and date." props={['signatureData', 'signerName', 'signerTitle', 'date', 'style']} note="Rendered as an embedded image in PDF and DOCX exports" />
      </div>

      <H3>Image Content Interface</H3>
      <CodeBlock code={`interface ImageContent {
  src: string;          // URL or base64 data URI
  alt?: string;
  caption?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none';
  borderRadius?: number;
  border?: string;
  opacity?: number;     // 0–1
}`} />
    </div>
  ),

  'chart-blocks': (
    <div>
      <SectionTitle subtitle="Data visualization blocks including charts, tables, metrics, and timelines.">
        Charts & Data Blocks
      </SectionTitle>
      <div className="grid grid-cols-1 gap-3">
        <BlockCard type="chart" label="Chart" description="Interactive data chart. Supports bar, line, pie, doughnut, area, and scatter types via Chart.js." props={['chartType', 'datasets[]', 'labels[]', 'title', 'legend', 'colors[]', 'xAxisLabel', 'yAxisLabel']} note="Charts render as canvas images in PDF and DOCX exports" />
        <BlockCard type="table" label="Data Table" description="Structured data table with configurable headers, rows, striping, and borders." props={['headers[]', 'rows[][]', 'caption', 'striped', 'bordered', 'compact', 'headerBg']} />
        <BlockCard type="metrics" label="Metrics / KPI Cards" description="Key performance indicator cards with value, label, trend arrow, and icon." props={['items[].value', 'items[].label', 'items[].trend', 'items[].icon', 'items[].color', 'columns']} />
        <BlockCard type="progress-bar" label="Progress Bar" description="Horizontal progress indicator with percentage display and animated fill." props={['value (0–100)', 'label', 'color', 'showPercentage', 'height', 'animated', 'rounded']} />
        <BlockCard type="timeline" label="Timeline" description="Vertical chronological timeline. Each event has a title, date, description, and color dot." props={['items[].title', 'items[].date', 'items[].description', 'items[].color', 'orientation']} />
      </div>

      <H3>Chart Types</H3>
      <div className="flex flex-wrap gap-2 mt-3">
        {['bar', 'horizontalBar', 'line', 'area', 'pie', 'doughnut', 'scatter', 'radar'].map(t => (
          <code key={t} className="bg-purple-50 border border-purple-200 text-purple-700 px-2.5 py-1 rounded-md text-xs font-mono">{t}</code>
        ))}
      </div>
    </div>
  ),

  'design-blocks': (
    <div>
      <SectionTitle subtitle="Visual design blocks for buttons, badges, callouts, shapes, and spacing.">
        Design Elements
      </SectionTitle>
      <div className="grid grid-cols-1 gap-3">
        <BlockCard type="button" label="Button" description="Clickable button with URL link. Multiple variants: primary, secondary, outline, ghost, destructive." props={['label', 'url', 'variant', 'size', 'color', 'borderRadius', 'icon', 'fullWidth']} note="Links activate in Preview mode and HTML export" />
        <BlockCard type="badge" label="Badge / Tag" description="Small inline status badge or label for categories, tags, or status indicators." props={['text', 'color', 'variant', 'size', 'icon']} />
        <BlockCard type="callout" label="Callout / Alert" description="Highlighted information box with icon and colored accent. Types: info, warning, success, error, note." props={['content', 'type', 'title', 'icon', 'backgroundColor', 'borderColor']} note="Supports {{variable_name}} in content" />
        <BlockCard type="spacer" label="Spacer" description="Invisible vertical spacing block. Adds whitespace between content sections." props={['height (px)']} aiSupport={false} exportSupport={false} />
        <BlockCard type="shape" label="Shape / Decoration" description="Geometric shape for visual decoration. Supports rectangle, circle, triangle, and line." props={['shapeType', 'color', 'borderRadius', 'border', 'opacity', 'rotation']} aiSupport={false} />
      </div>

      <H3>Callout Types</H3>
      <div className="space-y-2 mt-3">
        {[
          { type: 'info', color: 'text-blue-700 bg-blue-50 border-blue-200', label: '💬 info — General information, tips' },
          { type: 'warning', color: 'text-amber-700 bg-amber-50 border-amber-200', label: '⚠️ warning — Important caveats, risks' },
          { type: 'success', color: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: '✅ success — Positive outcomes, achievements' },
          { type: 'error', color: 'text-red-700 bg-red-50 border-red-200', label: '❌ error — Errors, critical issues' },
          { type: 'note', color: 'text-purple-700 bg-purple-50 border-purple-200', label: '📝 note — Side notes, additional context' },
        ].map(c => (
          <div key={c.type} className={`px-3 py-2 rounded-lg border text-xs font-medium ${c.color}`}>{c.label}</div>
        ))}
      </div>
    </div>
  ),

  'layout-blocks': (
    <div>
      <SectionTitle subtitle="Blocks for multi-column layouts, containers, page breaks, and AI section targeting.">
        Layout & Structure Blocks
      </SectionTitle>
      <div className="grid grid-cols-1 gap-3">
        <BlockCard type="columns" label="Columns" description="Multi-column layout container. Supports 2–4 columns with custom width ratios. Each column holds any block type." props={['columnCount (2–4)', 'columnRatios[]', 'gap', 'columns[].blocks[]']} note="Nested blocks within columns have their own independent properties" />
        <BlockCard type="card" label="Card / Container" description="Content container with background, border, shadow, and padding. Groups related content visually." props={['title', 'blocks[]', 'backgroundColor', 'borderColor', 'shadow', 'padding', 'borderRadius']} />
        <BlockCard type="page-break" label="Page Break" description="Forces a new page at this position in PDF and DOCX exports. Completely invisible in the HTML export." props={['style']} aiSupport={false} />
        <BlockCard type="section-marker" label="Section Marker" description="Invisible AI targeting marker. Defines the logical start of a named section. NEVER appears in any export." props={['sectionId', 'sectionName', 'description', 'aiInstructions']} exportSupport={false} />
      </div>

      <H3>Section Marker — Key Details</H3>
      <InfoBox type="warn">
        Section markers are always excluded from HTML, PDF, and DOCX exports. They exist solely as AI navigation markers.
      </InfoBox>
      <p className="text-gray-600 text-sm mb-3">
        Section markers define logical regions that AI can target for regeneration. Every section starts with a <code className="bg-gray-100 px-1 rounded text-xs font-mono">section-marker</code> block.
      </p>
      <CodeBlock code={`// Section marker block structure
{
  type: 'section-marker',
  content: {
    sectionId: 'executive-summary',
    sectionName: 'Executive Summary',
    description: 'High-level overview of findings',
    aiInstructions: 'Write 2–3 paragraphs. Lead with the key finding. Reference {{client_name}}.'
  }
}

// Typical section layout in a template:
[
  { type: 'section-marker', content: { sectionId: 'intro', sectionName: 'Introduction' } },
  { type: 'heading', content: { text: 'Introduction' } },
  { type: 'text', content: { text: 'Welcome {{client_name}}...' } },

  { type: 'section-marker', content: { sectionId: 'findings', sectionName: 'Key Findings' } },
  { type: 'heading', content: { text: 'Key Findings' } },
  // ...more blocks
]`} />
    </div>
  ),

  'special-blocks': (
    <div>
      <SectionTitle subtitle="Advanced blocks for markdown rendering and icon-text combinations.">
        Special Blocks
      </SectionTitle>
      <div className="grid grid-cols-1 gap-3 mb-6">
        <BlockCard type="markdown" label="Markdown Block" description="Renders full Markdown syntax to HTML. Supports GFM including tables, task lists, code fences, and strikethrough." props={['content (markdown string)', 'theme', 'fontSize']} note="Best for long-form AI-generated content. Variables work inside markdown." />
        <BlockCard type="icon-text" label="Icon + Text" description="Paired icon and text in a single block. Ideal for feature lists, contact info rows, or labeled values." props={['icon', 'text', 'iconColor', 'iconSize', 'layout']} />
      </div>

      <H3>Markdown Block — Use Cases</H3>
      <div className="space-y-2">
        {[
          'AI-generated long-form content (avoids generating many individual blocks)',
          'Rich text with complex formatting (nested lists, tables, code)',
          'Content imported from external Markdown sources',
          'Documentation sections that may be updated frequently',
        ].map(u => (
          <div key={u} className="flex gap-2 text-sm text-gray-600">
            <span className="text-emerald-500 shrink-0">✓</span>
            {u}
          </div>
        ))}
      </div>

      <H3>Markdown Example</H3>
      <CodeBlock code={`// Markdown block content
{
  type: 'markdown',
  content: {
    content: \`
## Summary for {{client_name}}

| Metric | Value |
|--------|-------|
| Score  | 92    |
| Grade  | A+    |

> **Note:** This report was generated on {{date}}.

- Item one
- Item two with **bold text**
    \`
  }
}`} />
    </div>
  ),

  variables: (
    <div>
      <SectionTitle subtitle="Dynamic content substitution using the {{variable_name}} syntax.">
        Variable System
      </SectionTitle>

      <H3>Syntax</H3>
      <p className="text-gray-600 text-sm mb-3 leading-relaxed">Variables use double curly brace syntax and are resolved at render time. They work in most text-based blocks:</p>
      <CodeBlock code={`// In any text block content:
"Hello {{client_name}}, your {{project_name}} report is ready."
"Report Date: {{date}}"
"Total Investment: \${{total_cost}}"
"Project Status: {{status}}"
"Prepared by: {{consultant_name}}, {{consultant_title}}"` } lang="text" />

      <H3>Defining Variables</H3>
      <p className="text-gray-600 text-sm mb-3">Use <code className="bg-gray-100 px-1 rounded text-xs font-mono text-blue-700">predefinedVariables</code> in config (not <code className="bg-red-50 px-1 rounded text-xs font-mono text-red-600">variables</code>):</p>
      <CodeBlock code={`config={{
  predefinedVariables: [
    {
      name: 'client_name',
      type: 'text',
      defaultValue: 'Client Name',
      label: 'Client Company Name',
      required: true,
    },
    { name: 'date', type: 'date', defaultValue: new Date().toISOString() },
    { name: 'total_cost', type: 'number', defaultValue: '0' },
    {
      name: 'status',
      type: 'list',
      options: ['Active', 'Complete', 'On Hold'],
      defaultValue: 'Active',
    },
  ]
}}`} />

      <H3>Supported Block Types</H3>
      <p className="text-gray-600 text-sm mb-3">Variable substitution is active in these block types:</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {['text', 'heading', 'subheading', 'blockquote', 'callout', 'bullet-list', 'numbered-list', 'markdown', 'table'].map(t => (
          <code key={t} className="bg-blue-50 border border-blue-200 text-blue-700 px-2.5 py-1 rounded-md text-xs font-mono">{t}</code>
        ))}
      </div>

      <InfoBox type="warn">
        <strong>Critical Rule:</strong> Never modify or remove <code className="bg-amber-100 px-1 rounded text-xs font-mono">{'{{variable_name}}'}</code> placeholders in block content. They must remain intact exactly as written for runtime substitution to work.
      </InfoBox>

      <H3>Variable Resolution Order</H3>
      <ol className="space-y-2 text-sm text-gray-600 mt-3">
        {[
          'Runtime variable values passed dynamically at load time',
          'User-edited values changed in the Variable Panel',
          'Default values from predefinedVariables config',
          'If no default: renders as empty string (no visible placeholder)',
        ].map((step, i) => (
          <li key={i} className="flex gap-3 items-start">
            <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
            {step}
          </li>
        ))}
      </ol>
    </div>
  ),

  'ai-regen': (
    <div>
      <SectionTitle subtitle="Section-aware AI content regeneration built directly into the editor.">
        AI Regeneration
      </SectionTitle>

      <H3>How It Works</H3>
      <div className="space-y-3 mb-6">
        {[
          ['Open AI Panel', 'Click the AI button in the toolbar. The AI Regeneration Panel slides in from the right.'],
          ['Select a Section', 'Choose a specific section (via section-marker) or regenerate the entire document.'],
          ['Enter Instructions', 'Provide natural language instructions for what to generate or change.'],
          ['AI Returns Blocks', 'Your onAIRegenerate callback receives the request, calls your AI, and returns new blocks.'],
          ['Editor Updates', 'The editor replaces the targeted section blocks with the AI-generated ones.'],
        ].map(([title, desc], i) => (
          <div key={title as string} className="flex gap-3 items-start">
            <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</div>
            <div>
              <div className="font-medium text-gray-900 text-sm">{title}</div>
              <div className="text-gray-500 text-xs mt-0.5 leading-relaxed">{desc}</div>
            </div>
          </div>
        ))}
      </div>

      <H3>Enabling AI Regeneration</H3>
      <CodeBlock code={`config={{
  allowAIRegeneration: true,
  onAIRegenerate: async (request) => {
    // request.blocks       — current blocks in targeted section
    // request.sectionId    — 'all' or section-marker's sectionId
    // request.sectionName  — human-readable section name
    // request.instructions — user's natural language instructions
    // request.variables    — current variable values {name: value}

    const response = await fetch('/api/ai/regenerate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    const { blocks } = await response.json();
    return { blocks };
  }
}}`} />

      <H3>Request & Response Types</H3>
      <CodeBlock code={`interface AIRegenerationRequest {
  blocks: Block[];
  sectionId: string;
  sectionName: string;
  instructions?: string;
  variables: Record<string, string>;
  context?: string;
}

interface AIRegenerationResponse {
  blocks: Block[];   // Replacement blocks for the section
  message?: string;  // Optional status message
}`} />

      <H3>Rules for AI-Generated Blocks</H3>
      <div className="space-y-2 mt-3">
        {[
          ['✅', 'Keep all section-marker blocks exactly at the start of each section'],
          ['✅', 'Preserve all {{variable_name}} placeholders verbatim'],
          ['✅', 'Use only valid block types from the block reference'],
          ['✅', 'Include the section-marker as the first block in each regenerated section'],
          ['⚠️', 'Use page-break and spacer blocks sparingly'],
          ['❌', 'Do not remove or rename section-marker blocks'],
          ['❌', 'Do not modify {{variable}} syntax — always preserve exactly as-is'],
        ].map(([icon, rule]) => (
          <div key={rule as string} className="flex gap-2 text-sm">
            <span className="shrink-0 w-6">{icon}</span>
            <span className="text-gray-600">{rule}</span>
          </div>
        ))}
      </div>
    </div>
  ),

  export: (
    <div>
      <SectionTitle subtitle="Export documents to HTML, PDF, and DOCX formats from the toolbar.">
        Export & Download
      </SectionTitle>

      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          { format: 'HTML', emoji: '🌐', desc: 'Complete self-contained web page with all styles inline', features: ['All 27 block types', 'Variables resolved', 'Fonts embedded', 'Links active', 'Full fidelity'], color: 'border-blue-200 bg-blue-50' },
          { format: 'PDF', emoji: '📄', desc: 'Print-ready PDF via browser print API with page sizing', features: ['All static blocks', 'Page breaks honored', 'Charts as images', 'Exact page sizing', 'Print-optimized'], color: 'border-emerald-200 bg-emerald-50' },
          { format: 'DOCX', emoji: '📝', desc: 'Microsoft Word compatible document for further editing', features: ['Text blocks', 'Images (embedded)', 'Tables', 'Headings & lists', 'Basic formatting'], color: 'border-purple-200 bg-purple-50' },
        ].map(f => (
          <div key={f.format} className={`border rounded-xl p-4 ${f.color}`}>
            <div className="text-3xl mb-2">{f.emoji}</div>
            <div className="font-bold text-gray-900 text-base mb-1">{f.format}</div>
            <div className="text-gray-500 text-xs mb-3 leading-relaxed">{f.desc}</div>
            <ul className="space-y-1">
              {f.features.map(s => (
                <li key={s} className="text-xs text-gray-600 flex gap-1.5 items-center">
                  <span className="text-emerald-500">✓</span>{s}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <H3>Blocks Excluded from Exports</H3>
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { label: 'section-marker', desc: 'AI only — never renders' },
          { label: 'video (PDF/DOCX)', desc: 'Replaced with placeholder' },
          { label: 'youtube (PDF/DOCX)', desc: 'Replaced with thumbnail' },
          { label: 'spacer (optional)', desc: 'May add margin instead' },
        ].map(b => (
          <div key={b.label} className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <code className="text-red-700 text-xs font-mono block">{b.label}</code>
            <span className="text-red-500 text-xs">{b.desc}</span>
          </div>
        ))}
      </div>

      <H3>Programmatic Export</H3>
      <CodeBlock code={`const editorRef = useRef<TemplateEditorHandle>(null);

// Get HTML string
const html = await editorRef.current?.exportHTML();

// Trigger PDF (opens browser print dialog)
await editorRef.current?.exportPDF();

// Download DOCX file
await editorRef.current?.exportDOCX();`} />

      <InfoBox type="tip">
        For best PDF quality, use <code className="bg-emerald-100 px-1 rounded text-xs font-mono">page-break</code> blocks to control pagination and set your page template to match your target paper size before exporting.
      </InfoBox>
    </div>
  ),

  'page-templates': (
    <div>
      <SectionTitle subtitle="Available page sizes and orientations for your documents.">
        Page Templates
      </SectionTitle>

      <div className="overflow-hidden rounded-xl border border-gray-200 mb-6">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-700">Template ID</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-700">Name</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-700">Width × Height (px)</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-700">Category</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {[
              ['a4-portrait', 'A4 Portrait', '794 × 1123', 'Standard', 'blue'],
              ['a4-landscape', 'A4 Landscape', '1123 × 794', 'Standard', 'blue'],
              ['us-letter-portrait', 'US Letter Portrait', '816 × 1056', 'Standard', 'blue'],
              ['us-letter-landscape', 'US Letter Landscape', '1056 × 816', 'Standard', 'blue'],
              ['us-legal', 'US Legal', '816 × 1344', 'Standard', 'blue'],
              ['a3-portrait', 'A3 Portrait', '1123 × 1587', 'Large', 'orange'],
              ['a5-portrait', 'A5 Portrait', '560 × 794', 'Small', 'gray'],
              ['presentation-16-9', 'Presentation 16:9', '1280 × 720', 'Presentation', 'purple'],
              ['presentation-4-3', 'Presentation 4:3', '1024 × 768', 'Presentation', 'purple'],
              ['social-square', 'Social Square', '1080 × 1080', 'Social Media', 'green'],
              ['social-story', 'Social Story (9:16)', '1080 × 1920', 'Social Media', 'green'],
              ['banner-wide', 'Wide Banner', '1920 × 400', 'Web', 'amber'],
              ['custom', 'Custom Size', 'User-defined', 'Custom', 'gray'],
            ].map(([id, name, size, cat, color]) => (
              <tr key={id as string} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-blue-700">{id}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{name}</td>
                <td className="px-4 py-3 font-mono text-gray-500">{size}</td>
                <td className="px-4 py-3"><Tag color={color as string}>{cat}</Tag></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H3>Setting Default Page Template</H3>
      <CodeBlock code={`config={{
  defaultPageTemplate: 'a4-portrait',  // Set on editor init
}}

// Change page template at runtime via the page settings toolbar
// Click the page icon → "Change Template" → select new size`} />
    </div>
  ),

  keyboard: (
    <div>
      <SectionTitle subtitle="All keyboard shortcuts for efficient editing.">
        Keyboard Shortcuts
      </SectionTitle>

      {[
        {
          group: 'Document',
          shortcuts: [
            [['Ctrl', 'S'], 'Save document'],
            [['Escape'], 'Deselect block / close active panel'],
          ]
        },
        {
          group: 'History',
          shortcuts: [
            [['Ctrl', 'Z'], 'Undo'],
            [['Ctrl', 'Y'], 'Redo'],
            [['Ctrl', 'Shift', 'Z'], 'Redo (alternate)'],
          ]
        },
        {
          group: 'Block Operations',
          shortcuts: [
            [['Ctrl', 'C'], 'Copy selected block'],
            [['Ctrl', 'X'], 'Cut selected block'],
            [['Ctrl', 'V'], 'Paste block'],
            [['Delete'], 'Delete selected block'],
            [['Ctrl', 'D'], 'Duplicate selected block'],
          ]
        },
        {
          group: 'View & Zoom',
          shortcuts: [
            [['Ctrl', '+'], 'Zoom in'],
            [['Ctrl', '-'], 'Zoom out'],
            [['Ctrl', '0'], 'Reset zoom to 100%'],
            [['Ctrl', 'Shift', 'F'], 'Fit canvas to viewport'],
          ]
        },
        {
          group: 'Block Navigation',
          shortcuts: [
            [['Tab'], 'Select next block'],
            [['Shift', 'Tab'], 'Select previous block'],
            [['Arrow Keys'], 'Nudge selected block 1px'],
            [['Shift', '↑↓←→'], 'Nudge selected block 10px'],
          ]
        },
        {
          group: 'Text Formatting (in text edit mode)',
          shortcuts: [
            [['Ctrl', 'B'], 'Bold'],
            [['Ctrl', 'I'], 'Italic'],
            [['Ctrl', 'U'], 'Underline'],
          ]
        },
      ].map(({ group, shortcuts }) => (
        <div key={group} className="mb-6">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">{group}</h3>
          <div className="border border-gray-100 rounded-xl overflow-hidden">
            {shortcuts.map(([keys, desc], i) => (
              <div key={desc as string} className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? 'border-t border-gray-100' : ''} hover:bg-gray-50`}>
                <span className="text-gray-700 text-sm">{desc}</span>
                <div className="flex items-center gap-1">
                  {(keys as string[]).map((key, ki) => (
                    <React.Fragment key={ki}>
                      <KbdKey>{key}</KbdKey>
                      {ki < (keys as string[]).length - 1 && <span className="text-gray-300 text-xs">+</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  ),

  advanced: (
    <div>
      <SectionTitle subtitle="Power features for fine-grained document control.">
        Advanced Features
      </SectionTitle>

      <H3>Undo / Redo History</H3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4">
        Full undo/redo history tracks up to <strong>50 states</strong>. Every operation — block add/delete/move/resize, property change, content edit — is recorded.
        Use <KbdKey>Ctrl+Z</KbdKey> / <KbdKey>Ctrl+Y</KbdKey> or the toolbar buttons.
      </p>

      <H3>Zoom & Viewport</H3>
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mb-4 text-sm text-gray-600">
        <ul className="space-y-1.5">
          <li>• Zoom range: <strong>10% – 500%</strong> via toolbar slider</li>
          <li>• <KbdKey>Ctrl+0</KbdKey> resets zoom to 100%</li>
          <li>• <KbdKey>Ctrl+Shift+F</KbdKey> fits the canvas to the viewport</li>
          <li>• Zoom level is preserved between sessions</li>
        </ul>
      </div>

      <H3>Grid & Snap-to-Grid</H3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4">
        Toggle the grid overlay and snap-to-grid from the toolbar. Default grid size is <strong>10px</strong>. Snap aligns block edges to grid lines for precise placement. Grid is not visible in exports.
      </p>

      <H3>Block Locking</H3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4">
        Lock a block via the property panel or right-click menu. Locked blocks display a lock icon overlay and cannot be moved, resized, or deleted until explicitly unlocked. Content editing is also disabled.
      </p>

      <H3>Block Visibility Toggle</H3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4">
        Hide blocks from the canvas without deleting them. Hidden blocks are completely excluded from exports but remain in the editor state. Useful for draft content or conditional sections.
      </p>

      <H3>Z-Index / Layer Order</H3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4">
        Control stacking order via the property panel. Actions: <em>Bring to Front</em>, <em>Bring Forward</em>, <em>Send Backward</em>, <em>Send to Back</em>, or set a specific numeric z-index.
      </p>

      <H3>Google Fonts Integration</H3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4">
        Access 1,000+ Google Fonts in the font picker. Fonts load on-demand when selected. Font choices are embedded as <code className="bg-gray-100 px-1 rounded text-xs font-mono">{'<link>'}</code> tags in HTML exports for full fidelity.
      </p>

      <H3>Multi-Page Documents</H3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4">
        Add unlimited pages via the Page Navigator panel (left sidebar). Each page has independent settings: background color/image, margins, and page template. Use <code className="bg-gray-100 px-1 rounded text-xs font-mono">page-break</code> blocks for explicit pagination control in exports.
      </p>

      <H3>Global Typography</H3>
      <p className="text-gray-600 text-sm leading-relaxed mb-4">
        Set document-wide default typography (font family, size, weight, line height) from the toolbar. Individual blocks can override these defaults via their own style properties.
      </p>

      <H3>Clipboard</H3>
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-sm text-gray-600">
        <ul className="space-y-1.5">
          <li>• Copy/paste blocks within the editor preserving all properties</li>
          <li>• Paste images from system clipboard directly onto canvas</li>
          <li>• Cross-page pasting is fully supported</li>
          <li>• Pasted blocks appear at a slight offset from the original</li>
        </ul>
      </div>
    </div>
  ),
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface DocsPanelProps {
  onClose: () => void;
}

export const DocsPanel: React.FC<DocsPanelProps> = ({ onClose }) => {
  const [activeId, setActiveId] = useState<string>('introduction');
  const [query, setQuery] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll content to top when section changes
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [activeId]);

  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const filteredGroups = useMemo(() => {
    if (!query.trim()) return NAV_GROUPS;
    const q = query.toLowerCase();
    return NAV_GROUPS.map(group => ({
      ...group,
      items: group.items.filter(
        item => item.title.toLowerCase().includes(q) || item.keywords?.some(k => k.includes(q))
      ),
    })).filter(group => group.items.length > 0);
  }, [query]);

  const currentContent = SECTIONS[activeId];

  const handleNavClick = (id: string) => {
    setActiveId(id);
    setQuery('');
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex" onClick={onClose}>
      <div
        className="flex-1 flex flex-col bg-white shadow-2xl overflow-hidden max-w-[1440px] mx-auto w-full"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center gap-3 px-5 py-3.5 bg-gray-950 text-white shrink-0 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-base">Template Editor</span>
            <ChevronRight className="w-4 h-4 text-gray-500" />
            <span className="text-gray-400 text-sm">Documentation</span>
          </div>

          <div className="flex-1 flex justify-center px-4">
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                autoFocus
                className="w-full bg-gray-800 text-white placeholder-gray-500 pl-9 pr-4 py-1.5 rounded-lg text-sm border border-gray-700 focus:border-blue-500 focus:outline-none transition-colors"
                placeholder="Search docs…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              {query && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  onClick={() => setQuery('')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            className="ml-auto p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex flex-1 overflow-hidden">

          {/* ── Sidebar ── */}
          <div className="w-56 bg-gray-50 border-r border-gray-200 overflow-y-auto shrink-0 py-3">
            {filteredGroups.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">
                No results for "{query}"
              </div>
            ) : (
              filteredGroups.map(group => (
                <div key={group.label} className="mb-4">
                  <div className="px-4 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    {group.label}
                  </div>
                  <div className="mt-0.5 space-y-0.5 px-2">
                    {group.items.map(item => {
                      const Icon = item.icon;
                      const isActive = activeId === item.id && !query;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleNavClick(item.id)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-all ${
                            isActive
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                          }`}
                        >
                          <Icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-blue-200' : 'text-gray-400'}`} />
                          <span className="truncate">{item.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Main Content ── */}
          <div ref={contentRef} className="flex-1 overflow-y-auto bg-white">
            <div className="max-w-3xl mx-auto px-8 py-8">
              {query && filteredGroups.length > 0 ? (
                <div>
                  <div className="mb-6 pb-4 border-b border-gray-100">
                    <h2 className="text-xl font-bold text-gray-900">Search Results</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {filteredGroups.reduce((acc, g) => acc + g.items.length, 0)} sections match "{query}"
                    </p>
                  </div>
                  <div className="space-y-2">
                    {filteredGroups.map(group =>
                      group.items.map(item => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.id}
                            onClick={() => handleNavClick(item.id)}
                            className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left group"
                          >
                            <div className="w-9 h-9 rounded-lg bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center shrink-0 transition-colors">
                              <Icon className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">{item.title}</div>
                              <div className="text-xs text-gray-400">{group.label}</div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400 ml-auto" />
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : (
                currentContent
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DocsPanel;
